<template>
  <div
    class="stacked-container"
    :class="[
      `stack-place-${placement}`,
      {
        'is-editor-unsized': showEditorFallback,
        [MAX_WIDTH_ALIGNED_CLASS]: isMaxWidthAligned(rootStyle),
      },
    ]"
    :style="rootStyle"
  >
    <slot />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  getMaxWidthMarginStyleData,
  getSizeStyleData,
  getSpacingStyleData,
} from '@render/composables/useStyleData'
import { MAX_WIDTH_ALIGNED_CLASS, isMaxWidthAligned } from '@render/lib/maxWidthAlign'
import { PipeDisplayMode, usePipe } from '@render/composables/usePipe'
import type { StackedChildPlacement, StackedContainerFields as Model } from './definition'

const fields = defineModel<Model>('fields', {
  default: () => ({}),
})

const pipe = usePipe()

const placement = computed<StackedChildPlacement>(() => fields.value?.childPlacement ?? 'stretch')

const explicitHeight = computed(
  () => getSizeStyleData(fields.value?.height) ?? getSizeStyleData(fields.value?.minHeight),
)

// Every child is out of flow, so nothing inside the box contributes height —
// an unsized stack collapses to 0px, which in the editor makes it invisible and
// impossible to drop into. Fall back to a visible box while authoring only
// (mirrors Carousel's editor-only empty state); the built pipe renders exactly
// what the author sized. `validations.ts` raises the matching warning so the
// fallback never quietly stands in for a real height.
const showEditorFallback = computed(
  () => pipe.currentMode.value === PipeDisplayMode.EDITOR && !explicitHeight.value,
)

const rootStyle = computed(() => {
  const f = fields.value
  const s: Record<string, string> = {}

  const width = getSizeStyleData(f?.width)
  if (width) s.width = width
  const minWidth = getSizeStyleData(f?.minWidth)
  if (minWidth) s.minWidth = minWidth
  const maxWidth = getSizeStyleData(f?.maxWidth)
  if (maxWidth) s.maxWidth = maxWidth
  const height = getSizeStyleData(f?.height)
  if (height) s.height = height
  const minHeight = getSizeStyleData(f?.minHeight)
  if (minHeight) s.minHeight = minHeight
  const maxHeight = getSizeStyleData(f?.maxHeight)
  if (maxHeight) s.maxHeight = maxHeight
  if (f?.overflow) s.overflow = f.overflow

  const padding = getSpacingStyleData(f?.padding)
  if (padding) s.padding = padding
  // The authored margin, and a capped box placed by its alignment. Unset keeps the
  // authored margin alone, as every StackedContainer with a max width rendered before the
  // alignment existed.
  Object.assign(s, getMaxWidthMarginStyleData(f?.margin, maxWidth, f?.maxWidthAlign, undefined))

  return s
})
</script>

<style scoped lang="scss">
.stacked-container {
  // The positioned ancestor every child resolves its offsets against. Note the
  // containing block is the PADDING box, so the `padding` field insets children
  // as well as ordinary content.
  position: relative;
  box-sizing: border-box;
}

.stacked-container > :deep(*) {
  position: absolute;
}

.stacked-container.stack-place-stretch > :deep(*) {
  inset: 0;
}

.stacked-container.stack-place-natural > :deep(*) {
  inset-block-start: 0;
  inset-inline-start: 0;
}

.stacked-container.stack-place-center > :deep(*) {
  top: 50%;
  left: 50%;
  translate: -50% -50%;
}

// `translate` composes with a child's own `transform`; engines without it (Chrome < 104,
// Safari < 14.1) drop it and leave the child hanging off the centre. The fallback replaces
// that transform instead, so an entrance animation runs off-centre there, then settles.
@supports not (translate: 0) {
  .stacked-container.stack-place-center > :deep(*) {
    transform: translate(-50%, -50%);
  }
}

.is-editor-unsized {
  min-height: 64px;
  outline: 1px dashed var(--color-input-border, #cbd5e1);
  outline-offset: -1px;
}
</style>
